import React, { useEffect, useState } from 'react';

import { MapTo, Utils } from '@adobe/aem-react-editable-components';
import { useCurrentContracts } from 'hooks/UseCurrentContracts';
import { useCurrentExperts } from 'hooks/UseCurrentExperts';
import { useCurrentInstaller } from 'hooks/UseCurrentInstaller';
import { isUndefined } from 'lodash';

import { DownloadInstallerIDItem } from 'components/DownloadInstallerIDItem/DownloadInstallerIDItem';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { RenderAemComponent } from 'components/RenderAemComponent/RenderAemComponent';
import { SubTitle } from 'components/SubTitle/SubTitle';

import { BRANCH_TYPE, MTN_OTHER_DOCUMENT_ID, TENANT_ID } from 'global/constants';

import { isDocumentDownloadElegible, prepareDocumentDownloadData } from 'utils/DocumentsUtils';
import { FFormDebug } from 'utils/FFormDebug';
import { getDEBPath } from 'utils/UrlUtil';
import { GRIDNET_PROVIDER, getTenantId } from 'utils/tenantUtil';

import './DocumentDownload.sass';

const DOCUMENTS = {
  [TENANT_ID.EDI]: [
    {
      id: GRIDNET_PROVIDER.EDI.EDI,
      name: 'Installateurausweis Strom',
      type: BRANCH_TYPE.ENERGY,
      templateId: 'EDI_MAIN_ENERGY',
    },
    {
      id: GRIDNET_PROVIDER.EDI.EDI,
      name: 'Installateurausweis Gas',
      type: BRANCH_TYPE.GAS,
      templateId: 'EDI_MAIN_GAS',
    },
    {
      id: GRIDNET_PROVIDER.EDI.EDI,
      name: 'Eintragungsbestätigung Gast Gas',
      type: BRANCH_TYPE.GAS,
      guest: true,
      templateId: 'EDI_GUEST_GAS',
    },
    {
      id: GRIDNET_PROVIDER.EDI.EDI,
      name: 'Eintragungsbestätigung Gast Strom',
      type: BRANCH_TYPE.ENERGY,
      guest: true,
      templateId: 'EDI_GUEST_ENERGY',
    },
  ],
  [TENANT_ID.SHNG]: [
    {
      id: GRIDNET_PROVIDER.SHNG,
      name: 'Information Gasteintragung Gas',
      guest: true,
      type: BRANCH_TYPE.GAS,
      templateId: 'SHNG_GUEST_GAS',
    },
    {
      id: GRIDNET_PROVIDER.SHNG,
      guest: true,
      name: 'Information Gasteintragung Strom',
      type: BRANCH_TYPE.ENERGY,
      templateId: 'SHNG_GUEST_ENERGY',
    },
    {
      id: GRIDNET_PROVIDER.SHNG.SHNG,
      name: 'SH-Netz Gas',
      type: BRANCH_TYPE.GAS,
      templateId: 'SHNG_MAIN_GAS',
    },
    {
      id: GRIDNET_PROVIDER.SHNG.SHNG,
      name: 'SH-Netz Strom',
      type: BRANCH_TYPE.ENERGY,
      templateId: 'SHNG_MAIN_ENERGY',
    },
    {
      id: GRIDNET_PROVIDER.SHNG.NNNG,
      name: 'NordNetz Strom',
      type: BRANCH_TYPE.ENERGY,
      templateId: 'NNNG_MAIN_ENERGY',
    },
    {
      id: GRIDNET_PROVIDER.SHNG.NNNG,
      name: 'NordNetz Gas',
      type: BRANCH_TYPE.GAS,
      templateId: 'NNNG_MAIN_GAS',
    },
    {
      id: GRIDNET_PROVIDER.SHNG.HANSG,
      name: 'HanseGas Gas',
      type: BRANCH_TYPE.GAS,
      templateId: 'HANSG_MAIN_GAS',
    },
    {
      id: GRIDNET_PROVIDER.SHNG.EENG,
      name: 'ElbEnergie Gas',
      type: BRANCH_TYPE.GAS,
      templateId: 'EENG_MAIN_GAS',
    },
  ],
  [TENANT_ID.AVA]: [
    {
      id: GRIDNET_PROVIDER.AVA.AVA,
      name: 'Installateurausweis Gas',
      type: BRANCH_TYPE.GAS,
      templateId: 'AVA_MAIN_GAS',
    },
    {
      id: GRIDNET_PROVIDER.AVA.AVA,
      name: 'Installateurausweis Strom',
      type: BRANCH_TYPE.ENERGY,
      templateId: 'AVA_MAIN_ENERGY',
    },
    {
      id: GRIDNET_PROVIDER.AVA.AVA,
      name: 'Eintragungsbestätigung Gast Gas',
      type: BRANCH_TYPE.GAS,
      guest: true,
      templateId: 'AVA_GUEST_GAS',
    },
    {
      id: GRIDNET_PROVIDER.AVA.AVA,
      name: 'Eintragungsbestätigung Gast Strom',
      type: BRANCH_TYPE.ENERGY,
      guest: true,
      templateId: 'AVA_GUEST_ENERGY',
    },
  ],
  [TENANT_ID.BAG]: [
    {
      id: GRIDNET_PROVIDER.BAG.BAG,
      name: 'Installateurausweis Gas',
      type: BRANCH_TYPE.GAS,
      templateId: 'BAG_MAIN_GAS',
    },
    {
      id: GRIDNET_PROVIDER.BAG.BAG,
      name: 'Installateurausweis Strom',
      type: BRANCH_TYPE.ENERGY,
      templateId: 'BAG_MAIN_ENERGY',
    },
    {
      id: GRIDNET_PROVIDER.BAG.BAG,
      name: 'Eintragungsbestätigung Gast Gas',
      type: BRANCH_TYPE.GAS,
      guest: true,
      templateId: 'BAG_GUEST_GAS',
    },
    {
      id: GRIDNET_PROVIDER.BAG.BAG,
      name: 'Eintragungsbestätigung Gast Strom',
      type: BRANCH_TYPE.ENERGY,
      guest: true,
      templateId: 'BAG_GUEST_ENERGY',
    },
  ],
  [TENANT_ID.LVN]: [
    {
      id: GRIDNET_PROVIDER.LVN.LVN,
      name: 'Eintragungsbestatigung Gast Strom',
      type: BRANCH_TYPE.ENERGY,
      guest: true,
      templateId: 'LVN_GUEST_ENERGY',
    },
    {
      id: GRIDNET_PROVIDER.LVN.LVN,
      name: 'Installateurausweis Strom',
      type: BRANCH_TYPE.ENERGY,
      templateId: 'LVN_MAIN_ENERGY',
    },
  ],
  [TENANT_ID.MTN]: [
    {
      id: GRIDNET_PROVIDER.MTN.MTNGAS,
      name: 'Installateurausweis Gas',
      type: BRANCH_TYPE.GAS,
      templateId: 'MTN_MAIN_GAS',
    },
    {
      id: GRIDNET_PROVIDER.MTN.MTNSTROM,
      name: 'Installateurausweis Strom',
      type: BRANCH_TYPE.ENERGY,
      templateId: 'MTN_MAIN_ENERGY',
    },
    {
      id: MTN_OTHER_DOCUMENT_ID,
      name: 'Installateurausweis Strom Andere',
      type: BRANCH_TYPE.ENERGY,
      templateId: 'MTN_MAIN_ENERGY_OTHER',
    },
    {
      id: GRIDNET_PROVIDER.MTN.MTNPLAUEN,
      name: 'Installateurausweis Strom Plauen',
      type: BRANCH_TYPE.ENERGY,
      guest: false,
      templateId: 'MTN_MAIN_PLAUEN',
    },
    {
      id: GRIDNET_PROVIDER.MTN.MTNGAS,
      name: 'Eintragungsbestätigung Gast Gas',
      type: BRANCH_TYPE.GAS,
      guest: true,
      templateId: 'MTN_GUEST_GAS',
    },
    {
      id: GRIDNET_PROVIDER.MTN.MTNSTROM,
      name: 'Eintragungsbestätigung Gast Strom',
      type: BRANCH_TYPE.ENERGY,
      guest: true,
      templateId: 'MTN_GUEST_ENERGY',
    },
  ],
};

export const DocumentDownload = () => {
  const CURRENT_DOCUMENTS = DOCUMENTS[getTenantId()];
  const [parsedData, setParsedData] = useState();
  const [isLoading, setIsLoading] = useState(true);

  const { installer, isLoading: isInstallerLoading } = useCurrentInstaller();
  const { experts, isLoading: isExpertsLoading } = useCurrentExperts();
  const { contracts, isLoading: isContractsLoading } = useCurrentContracts();

  useEffect(() => {
    (async () => {
      if (
        !isInstallerLoading &&
        installer &&
        !isExpertsLoading &&
        !isUndefined(experts) &&
        !isContractsLoading &&
        !isUndefined(contracts)
      ) {
        try {
          const data = prepareDocumentDownloadData(installer, experts, contracts);
          setParsedData(data);
          setIsLoading(false);
        } catch (e) {
          console.error('DocumentDownload data error', e);
          setIsLoading(false);
        }
      }
    })();
  }, [installer, experts, contracts, isInstallerLoading, isExpertsLoading, isContractsLoading]);

  const renderDownloadItems = (selectedExpert) => {
    const eligibleDocs = CURRENT_DOCUMENTS.filter((documentInfomation) => {
      return isDocumentDownloadElegible(documentInfomation, parsedData, selectedExpert);
    });

    if (eligibleDocs.length === 0) {
      return (
        <div className="no-file-info">
          Ihr Eintrag ist derzeit inaktiv. Zur Verlängerung der Fachkraft senden Sie uns bitte Ihre
          aktuellen Daten über <a href={getDEBPath()}>unser Formular</a>
        </div>
      );
    }

    return eligibleDocs.map((doc) => (
      <DownloadInstallerIDItem
        expert={selectedExpert}
        key={doc.name}
        documentInformation={doc}
        parsedData={parsedData}
      />
    ));
  };
  const isEditor = Utils.isInEditor();

  if (isEditor) {
    return <RenderAemComponent componentProps={{}} name="DocumentDownload" />;
  }
  if (isLoading || !parsedData) {
    return <LoadingIndicator />;
  }
  return (
    <div className="document-download">
      {parsedData.isEnergyActive || parsedData.isGasActive ? (
        <>
          {parsedData.installers
            .sort((firstExpert, secondExpert) =>
              firstExpert.lastname.localeCompare(secondExpert.lastname)
            )
            .map((selectedExpert, i) => (
              <div className="document-download__download-wrapper" key={i}>
                <SubTitle
                  notFramed
                  text={`${selectedExpert.firstname} ${selectedExpert.lastname}`}
                />
                <div className="document-download__download-section-wrapper">
                  {renderDownloadItems(selectedExpert)}
                </div>
              </div>
            ))}
          <FFormDebug values={parsedData} />
        </>
      ) : (
        <div>
          Ihre Ausweisgültigkeit ist abgelaufen, bitte verlängern Sie ihre Eintragung:{' '}
          <a href={getDEBPath()}>Zur Verlängerung</a>
        </div>
      )}
    </div>
  );
};

DocumentDownload.displayName = 'DocumentDownload';

DocumentDownload.propTypes = {};

DocumentDownload.defaultProps = {};

MapTo(['e-fix/components/content/documentdownload'])(DocumentDownload);
